import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  resolvePasswordResetRequestError,
} from "../../lib/authFormErrors";
import { isPasswordResetEnabled } from "../../lib/passwordResetEnabled";
import { requestPasswordReset } from "../../lib/passwordResetApi";
import PasswordResetUnavailable from "./PasswordResetUnavailable";

export default function ForgotPassword() {
  if (!isPasswordResetEnabled()) {
    return <PasswordResetUnavailable />;
  }

  return <ForgotPasswordForm />;
}

function ForgotPasswordForm() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const forgotSchema = z.object({
    email: z.string().email(t("login.invalidEmail")),
  });

  type ForgotForm = z.infer<typeof forgotSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotForm>({
    resolver: zodResolver(forgotSchema),
  });

  const onSubmit = async (data: ForgotForm) => {
    setFormError(null);
    setIsSending(true);
    try {
      await requestPasswordReset(data.email);
      setSubmitted(true);
    } catch (error) {
      const resolved = resolvePasswordResetRequestError(error);
      if (resolved) {
        setFormError(t(resolved.messageKey));
      } else {
        setSubmitted(true);
      }
    } finally {
      setIsSending(false);
    }
  };

  if (submitted) {
    return (
      <div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">
          {t("passwordReset.forgotTitle")}
        </h2>
        <p
          className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900"
          role="status"
        >
          {t("passwordReset.confirmation")}
        </p>
        <p className="mt-6 text-center">
          <Link to="/login" className="text-sm font-medium text-primary-600 hover:text-primary-700">
            {t("passwordReset.backToLogin")}
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-2 text-2xl font-bold text-gray-900">
        {t("passwordReset.forgotTitle")}
      </h2>
      <p className="mb-6 text-gray-600">{t("passwordReset.forgotSubtitle")}</p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {formError && (
          <p
            id="forgot-password-error"
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          >
            {formError}
          </p>
        )}

        <div>
          <label htmlFor="forgot-email" className="label">
            {t("login.email")}
          </label>
          <input
            {...register("email")}
            type="email"
            id="forgot-email"
            autoComplete="email"
            aria-invalid={Boolean(errors.email)}
            aria-describedby={
              errors.email ? "forgot-email-error" : formError ? "forgot-password-error" : undefined
            }
            className={`input ${errors.email ? "input-error" : ""}`}
          />
          {errors.email && (
            <p id="forgot-email-error" className="mt-1 text-sm text-red-500">
              {errors.email.message}
            </p>
          )}
        </div>

        <button type="submit" disabled={isSending} className="w-full btn-primary">
          {isSending ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t("passwordReset.sending")}
            </>
          ) : (
            t("passwordReset.submit")
          )}
        </button>
      </form>

      <p className="mt-6 text-center">
        <Link to="/login" className="text-sm font-medium text-primary-600 hover:text-primary-700">
          {t("passwordReset.backToLogin")}
        </Link>
      </p>
    </div>
  );
}
